import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { landingFor } from "../constants/personaRoutes";
import { FaUserCircle, FaExclamationTriangle, FaTimes, FaCheckCircle, FaSignOutAlt, FaUser, FaUserCog, FaPalette } from "react-icons/fa";
import { Loader2, ChevronsUpDown } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import useLogout from "../hooks/useLogout";
import axiosInstance from "../services/api/axios";
import { fetchLoginIdentifierLinks } from "../services/login";
import CustomerPickerModal from "./CustomerPickerModal";
import logo_name from '../assets/images/logo_name.png';
import logo from '../assets/images/logo.png';
import PortfolioSetupPrompt from './InteriorDesigner/PortfolioSetupPrompt';
import NotificationBell from './NotificationBell';

function Navbar() {
    const location = useLocation();
    const [isProfileMenuOpen, setProfileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const toggleProfileMenu = () => setProfileMenuOpen(!isProfileMenuOpen);

    const { userInfo, setUserInfo, Trial, switchLoginIdentifier } = useUser();
    const [subscriptionData, setSubscriptionData] = useState(null); // new state

    // Org switcher: the topbar chip IS the trigger — clicking it opens the
    // picker modal directly (no intermediate dropdown menu). Links come from
    // userInfo.identifierLinks (cached at login + after every switch); if
    // that's missing for any reason (e.g. stale session from before this
    // feature), we lazily fetch on the first chip click.
    const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false);
    const [orgLinks, setOrgLinks] = useState([]);
    const [orgLinksLoading, setOrgLinksLoading] = useState(false);

    // State to control onboarding banner visibility
    const [showOnboardingBanner, setShowOnboardingBanner] = useState(true);
    const [onboardingPopoverOpen, setOnboardingPopoverOpen] = useState(false);
    const popoverRef = useRef(null);
    const profileMenuRef = useRef(null);

    // Add state for projects to check email configuration
    const [projects, setProjects] = useState([]);

    // Interior Designer: portfolio onboarding state
    const [idPortfolioCount, setIdPortfolioCount] = useState(null); // null = not loaded yet
    const [idPortfolioPopoverOpen, setIdPortfolioPopoverOpen] = useState(false);
    const [showIdPortfolioBanner, setShowIdPortfolioBanner] = useState(true);
    const [showPortfolioPrompt, setShowPortfolioPrompt] = useState(false);
    const idPortfolioPopoverRef = useRef(null);

    // Close dropdowns on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            // Close onboarding popover
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setOnboardingPopoverOpen(false);
            }

            // Close interior designer portfolio popover
            if (
                idPortfolioPopoverRef.current &&
                !idPortfolioPopoverRef.current.contains(event.target)
            ) {
                setIdPortfolioPopoverOpen(false);
            }
            
            // Close profile menu
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setProfileMenuOpen(false);
            }
        }
        
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch projects to check email / WhatsApp onboarding flags; refetch when settings save (see Onboarding.jsx)
    useEffect(() => {
        const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
        const fetchProjectsData = async () => {
            if (!customerId) return;

            try {
                const response = await axiosInstance.get('/projects', {
                    params: { customerId }
                });
                // Handle both paginated and non-paginated responses
                const projectsArray = response?.items || (Array.isArray(response) ? response : []);
                setProjects(projectsArray);
            } catch (error) {
                console.error('Error fetching projects for email check:', error);
                setProjects([]);
            }
        };

        fetchProjectsData();

        const handleProjectsConfigurationUpdated = () => {
            fetchProjectsData();
        };

        window.addEventListener('projectsConfigurationUpdated', handleProjectsConfigurationUpdated);

        return () => {
            window.removeEventListener('projectsConfigurationUpdated', handleProjectsConfigurationUpdated);
        };
    }, [userInfo?.loginIdentifierDetails?.customer?.customerId]);

    // Interior Designer: fetch portfolio to know whether at least one item exists.
    // Drives both the navbar tag and the first-login setup prompt.
    useEffect(() => {
        const persona = userInfo?.persona;
        if (persona !== "INTERIOR_DESIGNER") {
            setIdPortfolioCount(null);
            return undefined;
        }

        let cancelled = false;
        const fetchPortfolio = async () => {
            try {
                const response = await axiosInstance.get('/interior-designers/portfolio');
                const list = Array.isArray(response)
                    ? response
                    : Array.isArray(response?.data)
                        ? response.data
                        : Array.isArray(response?.items)
                            ? response.items
                            : [];
                if (!cancelled) setIdPortfolioCount(list.length);
            } catch (error) {
                console.error('Error fetching interior designer portfolio for navbar:', error);
                // 404 / empty just means no portfolio yet
                if (!cancelled) setIdPortfolioCount(0);
            }
        };
        fetchPortfolio();

        const handlePortfolioUpdated = () => {
            fetchPortfolio();
        };
        window.addEventListener('idPortfolioUpdated', handlePortfolioUpdated);

        return () => {
            cancelled = true;
            window.removeEventListener('idPortfolioUpdated', handlePortfolioUpdated);
        };
    }, [userInfo?.persona, userInfo?.loginId]);

    // Show the first-login portfolio setup prompt once per user when their portfolio is empty.
    // Skip when the user is already on a portfolio-related route (would be redundant).
    useEffect(() => {
        if (userInfo?.persona !== "INTERIOR_DESIGNER") return;
        if (idPortfolioCount === null) return; // still loading
        if (idPortfolioCount > 0) return; // already has portfolio
        const path = location.pathname || "";
        const isOnPortfolioRoute =
            path.startsWith("/interior-designer/add-portfolio") ||
            path.startsWith("/interior-designer/edit-portfolio") ||
            (path === "/interior-designer/designs" && location.hash === "#portfolio");
        if (isOnPortfolioRoute) return;
        const loginIdKey = userInfo?.loginId ?? userInfo?.email ?? 'anon';
        const storageKey = `id_portfolio_prompt_seen_${loginIdKey}`;
        try {
            if (localStorage.getItem(storageKey)) return;
        } catch (e) {
            // localStorage unavailable — fall through and show prompt
        }
        setShowPortfolioPrompt(true);
    }, [idPortfolioCount, userInfo?.persona, userInfo?.loginId, userInfo?.email, location.pathname, location.hash]);

    const dismissPortfolioPrompt = useCallback(() => {
        const loginIdKey = userInfo?.loginId ?? userInfo?.email ?? 'anon';
        try {
            localStorage.setItem(`id_portfolio_prompt_seen_${loginIdKey}`, '1');
        } catch (e) {
            // ignore — at worst the prompt will reappear
        }
        setShowPortfolioPrompt(false);
    }, [userInfo?.loginId, userInfo?.email]);

    // Check if onboarding is incomplete
    const isOnboardingIncomplete = () => {
        if (!userInfo?.loginIdentifierDetails?.customer) return false;
        
        const customer = userInfo.loginIdentifierDetails.customer;
        
        // Check basic requirements
        const basicRequirementsIncomplete = !customer.isLetterheadFooterStampUploaded || 
                                         !customer.isSslCertificateGenerated;
        
        // Check if any project has email configuration
        const hasProjectEmailConfig = projects.some(project => 
            project?.socialMediaConfiguration?.email?.emailPasswordSecret
        );
        
        // Check if any project has WhatsApp configuration
        const hasProjectWhatsAppConfig = projects.some(project => 
            project?.socialMediaConfiguration?.whatsapp?.metaId &&
            project?.socialMediaConfiguration?.whatsapp?.whatsappSecretAccessToken &&
            project?.socialMediaConfiguration?.whatsapp?.whatsappBusinessAccountId
        );
        
        return basicRequirementsIncomplete || !hasProjectEmailConfig || !hasProjectWhatsAppConfig;
    };

    const getOnboardingIssues = () => {
        if (!userInfo?.loginIdentifierDetails?.customer) return [];
        
        const customer = userInfo.loginIdentifierDetails.customer;
        const issues = [];
        
        if (!customer.isLetterheadFooterStampUploaded) {
            issues.push("Company Documents");
        }
        if (!customer.isSslCertificateGenerated) {
            issues.push("SSL Certificate");
        }
        
        // Check if any project has email configuration
        const hasProjectEmailConfig = projects.some(project => 
            project?.socialMediaConfiguration?.email?.emailPasswordSecret
        );
        
        if (!hasProjectEmailConfig) {
            issues.push("Project Email Configuration");
        }
        
        // Check if any project has WhatsApp configuration
        const hasProjectWhatsAppConfig = projects.some(project => 
            project?.socialMediaConfiguration?.whatsapp?.metaId &&
            project?.socialMediaConfiguration?.whatsapp?.whatsappSecretAccessToken &&
            project?.socialMediaConfiguration?.whatsapp?.whatsappBusinessAccountId
        );
        
        if (!hasProjectWhatsAppConfig) {
            issues.push("Project WhatsApp Configuration");
        }
        
        return issues;
    };

    // The session teardown itself lives in useLogout, so the account-deletion flow can run
    // the identical sequence. A session spans React context, sessionStorage and two cookies;
    // a second hand-rolled copy would eventually miss one and leave the user half signed-in.
    // Closing the profile menu is NavBar's own concern, so it stays here.
    const clearSession = useLogout();
    const logout = useCallback(() => {
        setProfileMenuOpen(false);
        clearSession();
    }, [clearSession]);

    // Cached identifier links live on userInfo. When present and length>1
    // this is a true multi-org login and the chip should be clickable. We
    // keep a derived value rather than a separate state so a session-storage
    // hydrate (page reload) is reflected immediately.
    const cachedIdentifierLinks = Array.isArray(userInfo?.identifierLinks)
        ? userInfo.identifierLinks
        : null;
    const isMultiOrg = (cachedIdentifierLinks?.length ?? 0) > 1;
    // Unknown means the cache is missing (e.g. stale session predating this
    // feature). We let the chip be clickable in that case and lazily fetch
    // the list on click — showing a small inline spinner while we wait.
    const identifierLinksUnknown = cachedIdentifierLinks === null;

    // Open the org switcher modal. Uses the cached identifier-links when
    // available; only fires the GET when we genuinely don't have them.
    const handleOpenOrgSwitcher = useCallback(async () => {
        if (cachedIdentifierLinks && cachedIdentifierLinks.length > 1) {
            setOrgLinks(cachedIdentifierLinks);
            setOrgSwitcherOpen(true);
            return;
        }
        // Cache miss — lazily fetch.
        setOrgLinksLoading(true);
        try {
            const resp = await fetchLoginIdentifierLinks();
            const links = resp?.links || [];
            // Mirror onto userInfo so subsequent clicks hit the cache.
            setUserInfo((prev) => (prev ? { ...prev, identifierLinks: links } : prev));
            setOrgLinks(links);
            if (links.length <= 1) {
                toast.info("This login is linked to only one organization.");
                return;
            }
            setOrgSwitcherOpen(true);
        } catch (err) {
            console.error("Failed to load identifier links:", err);
            toast.error(
                err?.detail || err?.response?.data?.detail || "Could not load organizations."
            );
        } finally {
            setOrgLinksLoading(false);
        }
    }, [cachedIdentifierLinks, setUserInfo]);

    // Mirror of the modal's name-resolution: persona schemas place company
    // name either at the top level or nested under `customer`. Used to build
    // the post-switch toast.
    const resolveLinkOrgName = useCallback((link) => {
        const d = link?.identifierDetails || {};
        return (
            d.customer?.companyName ||
            d.companyName ||
            d.name ||
            d.customer?.name ||
            "the selected organization"
        );
    }, []);

    // Confirm an org switch. Keeps the old session intact if the new JWT
    // call fails (switchLoginIdentifier throws on bad response).
    const handleOrgSwitchConfirm = useCallback(
        async ({ linkId, remember }) => {
            try {
                const next = await switchLoginIdentifier({ linkId, remember });
                // Resolve the new org name from the in-modal list (which is
                // whatever we passed in via the `links` prop) — userInfo
                // hasn't necessarily re-rendered into our closure yet.
                const picked = orgLinks.find((l) => l.linkId === linkId);
                const newOrgName = resolveLinkOrgName(picked);
                setOrgSwitcherOpen(false);

                // Switching can now change PERSONA, not just organisation, because
                // persona lives on the identifier link. The current route almost
                // certainly belongs to the old persona, so a bare reload would land
                // the user on <Unauthorized/>.
                //
                // location.assign rather than navigate(): it keeps the 'bust every
                // org-scoped cache' behaviour the reload was there for, while still
                // landing somewhere the new persona owns.
                // Read from userInfo rather than the outer `persona` const: that is
                // declared further down the component, so touching it here (and in
                // the dependency array) hit its temporal dead zone and threw
                // "Cannot access 'persona' before initialization" on first render.
                const currentPersona = userInfo?.persona;
                const nextPersona = next?.persona ?? currentPersona;
                if (nextPersona !== currentPersona) {
                    window.location.assign(landingFor(nextPersona));
                    return;
                }
                toast.success(`Switched to ${newOrgName}`);
                window.location.reload();
            } catch (err) {
                console.error("Error switching organization:", err);
                toast.error(
                    err?.detail ||
                        err?.response?.data?.detail ||
                        "Could not switch organization. Your current session is unchanged."
                );
            }
        },
        [switchLoginIdentifier, orgLinks, resolveLinkOrgName, userInfo?.persona]
    );

    useEffect(() => {
        const handleForceLogout = () => {
         
            logout();
        };

        window.addEventListener('forceLogout', handleForceLogout);

        return () => {
            window.removeEventListener('forceLogout', handleForceLogout); // Clean up the event listener
        };
    }, [logout]);


    useEffect(() => {
        const fetchSubscriptionInfo = async () => {
            try {
                const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;

                if (!customerId) {
                    console.warn("Customer ID not found in userInfo. Cannot fetch subscription.");
                    return;
                }

                const response = await axiosInstance.get(`/subscriptions`, {
                    params: {
                        customerId: customerId,
                    },
                });

                const subscriptions = response;

                if (subscriptions && subscriptions.length > 0) {
                    const subscription = subscriptions[0];
                    setSubscriptionData(subscription);
                } else {
                    console.warn("No subscriptions found for customer ID:", customerId);
                }
            } catch (error) {
                console.error("Error fetching subscription info:", error);
            }
        };

        if (userInfo?.loginIdentifierDetails?.customer?.customerId) {
            fetchSubscriptionInfo();
        }
    }, [setUserInfo, userInfo?.loginIdentifierDetails?.customer?.customerId]);

    const persona = userInfo?.persona;
    const isInteriorDesignerNav = persona === "INTERIOR_DESIGNER";
    const isZumaleUserNav = persona === "ZUMALE_USER";
    const profileSettingsPath = isInteriorDesignerNav
        ? "/interior-designer/settings#profile"
        : "/profile#profile";

    const companyName =
        persona === "CHANNEL_PARTNER"
            ? userInfo?.loginIdentifierDetails?.companyName || "UNKNOWN"
            : userInfo?.loginIdentifierDetails?.customer?.companyName;


    const handleLogoClick = () => {
        // Shared map. The local copy this replaced sent BV Associates to
        // /bv-user-dashboard — a route that does not exist, so the logo landed
        // them on <Unauthorized/> — and had no entry at all for architects or
        // freelancers, who fell through to "/".
        navigate(landingFor(persona));
    };

    // --- Professional Onboarding Tag with Popover Checklist ---
    const onboardingSteps = [
        { key: "Company Documents", hash: "onboarding-documents" },
        { key: "SSL Certificate", hash: "onboarding-ssl" },
        { key: "Project Email Configuration", hash: "onboarding-email" },
        { key: "Project WhatsApp Configuration", hash: "onboarding-whatsapp" },
    ];
    const issues = getOnboardingIssues();
    const totalSteps = onboardingSteps.length;
    const completedSteps = totalSteps - issues.length;

    const OnboardingTag = () => (
        <div className="relative ml-2" ref={popoverRef}>
            <div className="flex items-center bg-amber-50 text-amber-900 rounded-full px-3 py-1 gap-2 border border-amber-200 shadow-sm hover:bg-amber-100 transition min-w-0">
                <FaExclamationTriangle className="text-amber-400 text-xs mr-1" style={{ minWidth: 16 }} />
                <span className="font-medium text-xs">Action Required</span>
                <span className="ml-1 text-xs text-amber-700">{completedSteps}/{totalSteps}</span>
                <span className="ml-1 w-2 h-2 bg-amber-400 rounded-full inline-block"></span>
                <button
                    className="ml-2 p-1 rounded-full hover:bg-amber-100 focus:bg-amber-200 focus:outline-none transition-colors"
                    onClick={() => setShowOnboardingBanner(false)}
                    aria-label="Dismiss onboarding alert"
                    tabIndex={0}
                    type="button"
                    style={{ lineHeight: 0 }}
                >
                    <FaTimes className="text-amber-400 text-xs" />
                </button>
            </div>
            <button
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onClick={() => setOnboardingPopoverOpen((o) => !o)}
                aria-label="Show onboarding details"
                tabIndex={-1}
                type="button"
                style={{ zIndex: 1 }}
            />
            {onboardingPopoverOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3 animate-fade-in">
                    <div className="mb-2 text-xs font-semibold text-black">
                        Complete your setup:
                    </div>
                    <ul className="space-y-2">
                        {onboardingSteps.map((step) => (
                            <li key={step.key} className="flex items-center gap-2">
                                {issues.includes(step.key) ? (
                                    <span className="w-4 h-4 flex items-center justify-center text-amber-400">
                                        <FaExclamationTriangle />
                                    </span>
                                ) : (
                                    <span className="w-4 h-4 flex items-center justify-center text-green-500">
                                        <FaCheckCircle />
                                    </span>
                                )}
                                <a
                                    href={`/profile#${step.hash}`}
                                    className={`text-xs hover:underline transition ${
                                        issues.includes(step.key)
                                            ? "text-amber-900 cursor-pointer"
                                            : "text-gray-400 pointer-events-none"
                                    }`}
                                    tabIndex={issues.includes(step.key) ? 0 : -1}
                                    aria-disabled={!issues.includes(step.key)}
                                >
                                    {step.key}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );

    // --- Interior Designer Portfolio Setup Tag ---
    // Mirrors OnboardingTag: amber pill + popover, but for the single "Add at least
    // one portfolio item" step. Visible only while the designer has zero portfolio items.
    const InteriorDesignerPortfolioTag = () => (
        <div className="relative ml-2" ref={idPortfolioPopoverRef}>
            <div className="flex items-center bg-amber-50 text-amber-900 rounded-full px-3 py-1 gap-2 border border-amber-200 shadow-sm hover:bg-amber-100 transition min-w-0">
                <FaPalette className="text-amber-500 text-xs mr-1" style={{ minWidth: 16 }} />
                <span className="font-medium text-xs">Set up portfolio</span>
                <span className="ml-1 text-xs text-amber-700">0/1</span>
                <span className="ml-1 w-2 h-2 bg-amber-400 rounded-full inline-block"></span>
                <button
                    className="ml-2 p-1 rounded-full hover:bg-amber-100 focus:bg-amber-200 focus:outline-none transition-colors"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIdPortfolioPopoverOpen(false);
                        setShowIdPortfolioBanner(false);
                    }}
                    aria-label="Dismiss portfolio setup alert"
                    tabIndex={0}
                    type="button"
                    style={{ lineHeight: 0 }}
                >
                    <FaTimes className="text-amber-400 text-xs" />
                </button>
            </div>
            <button
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onClick={() => setIdPortfolioPopoverOpen((o) => !o)}
                aria-label="Show portfolio setup details"
                tabIndex={-1}
                type="button"
                style={{ zIndex: 1 }}
            />
            {idPortfolioPopoverOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3 animate-fade-in">
                    <div className="mb-1 text-xs font-semibold text-black">
                        Complete your setup:
                    </div>
                    <p className="mb-3 text-xs text-gray-500 leading-relaxed">
                        Add at least one portfolio item so buyers can browse your work and you can attach designs to leads.
                    </p>
                    <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                            <span className="w-4 h-4 flex items-center justify-center text-amber-400">
                                <FaExclamationTriangle />
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    setIdPortfolioPopoverOpen(false);
                                    navigate('/interior-designer/add-portfolio');
                                }}
                                className="text-xs text-amber-900 hover:underline transition cursor-pointer text-left"
                            >
                                Add your first portfolio item
                            </button>
                        </li>
                    </ul>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => {
                                setIdPortfolioPopoverOpen(false);
                                navigate('/interior-designer/add-portfolio');
                            }}
                            className="w-full text-center text-xs font-semibold text-white bg-flamingo-500 hover:bg-flamingo-600 transition rounded px-3 py-1.5"
                        >
                            Set up now
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    const isInteriorDesignerPortfolioMissing =
        isInteriorDesignerNav && idPortfolioCount === 0 && showIdPortfolioBanner;

    // Derive the active link id by matching the persona-keyed identifier on
    // the user's loginIdentifierDetails against each link's details. The BE
    // does not echo link_id on /login/check-password, so we resolve client-
    // side. Returns undefined when no match (modal just won't highlight).
    const activeCustomerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
    const activeUserId = userInfo?.loginIdentifierDetails?.userId;
    const activeDesignerId = userInfo?.loginIdentifierDetails?.designerId;
    const currentLinkId = (() => {
        if (!orgLinks?.length) return undefined;
        const matched = orgLinks.find((l) => {
            const d = l?.identifierDetails || {};
            if (activeCustomerId && d.customer?.customerId === activeCustomerId) return true;
            if (activeUserId && d.userId === activeUserId) return true;
            if (activeDesignerId && d.designerId === activeDesignerId) return true;
            return false;
        });
        return matched?.linkId;
    })();

    return (
        <>
            <nav
                className="bg-white shadow-md w-full fixed z-40"
                style={{ top: 0 }}
            >
                <div className="flex justify-between items-center h-16 px-4 md:px-6 lg:px-8">
                    {/* Left: Logo */}
                    <div className="flex items-center flex-shrink-0">
                        <img src={logo} alt="Company Logo" className="h-8 w-auto mr-2 cursor-pointer" />
                        <div
                            onClick={handleLogoClick}
                            className="cursor-pointer"
                        >
                            <img src={logo_name} alt="Company Logo name" className="h-12 w-auto" />
                        </div>
                    </div>
                    
                    {/* Right: Company name, quota, onboarding tag, profile icon - Always at far right */}
                    <div className="flex items-center space-x-2 md:space-x-4 lg:space-x-6 ml-auto">
                        {/* Company Name Display */}
                         {/* Onboarding Tag (if needed) */}
                         {isOnboardingIncomplete() &&
                            (persona === "ZUMALE_USER" ||
                             persona === "DEVELOPER_ADMIN" ||
                             persona === "STAFF") &&
                            showOnboardingBanner && <OnboardingTag />}
                        {/* Interior Designer portfolio setup tag (if needed) */}
                        {isInteriorDesignerPortfolioMissing && <InteriorDesignerPortfolioTag />}
                        {/* Active organization chip. Clicking the chip
                            itself opens the picker modal — no intermediate
                            dropdown. For single-org logins this renders as a
                            plain non-interactive label (no caret, no hover).
                            The Profile shortcut lives inside the profile
                            button to the right of this chip. */}
                        {companyName && (() => {
                            const chipClickable = isMultiOrg || identifierLinksUnknown;
                            const truncateCls =
                                "truncate max-w-[80px] sm:max-w-[120px] md:max-w-[150px] lg:max-w-[200px]";
                            if (!chipClickable) {
                                return (
                                    <div
                                        className={`text-[#1d293b] uppercase font-bold text-xs md:text-sm lg:text-base ${truncateCls}`}
                                    >
                                        {companyName}
                                    </div>
                                );
                            }
                            return (
                                <button
                                    type="button"
                                    onClick={handleOpenOrgSwitcher}
                                    disabled={orgLinksLoading}
                                    className="group flex items-center gap-1.5 rounded-full bg-slate-100 hover:bg-flamingo-50 ring-1 ring-slate-200 hover:ring-flamingo-200 text-[#1d293b] uppercase font-bold text-xs md:text-sm px-3 py-1.5 transition-all focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-1 disabled:cursor-wait disabled:opacity-80"
                                    aria-haspopup="dialog"
                                    aria-label={`Switch organization (currently ${companyName})`}
                                    title="Click to switch organization"
                                >
                                    <span className={truncateCls}>{companyName}</span>
                                    {/* The double-chevron icon hints that the
                                        chip is interactive and switchable.
                                        Replaced by a spinner during the
                                        lazy-fetch of identifier links. */}
                                    {orgLinksLoading ? (
                                        <Loader2
                                            size={14}
                                            className="animate-spin opacity-70 shrink-0"
                                            aria-hidden
                                        />
                                    ) : (
                                        <ChevronsUpDown
                                            size={14}
                                            className="text-slate-500 group-hover:text-flamingo-600 transition-colors shrink-0"
                                            aria-hidden
                                        />
                                    )}
                                </button>
                            );
                        })()}
                        
                        {/* In-app notifications */}
                        <NotificationBell />

                        {/* Profile Icon with Dropdown */}
                        <div className="relative flex-shrink-0" ref={profileMenuRef}>
                            <button
                                onClick={toggleProfileMenu}
                                className="flex items-center space-x-2 text-[#1d293b] hover:text-stats-orange p-1 rounded-full hover:bg-gray-100 transition-colors"
                            >
                                <FaUserCircle size={20} className="md:w-5 md:h-5" />
                            </button>
                            {isProfileMenuOpen && (
                                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                                    {/* User Profile Section */}
                                    {(() => {
                                        // The popup is only 256px wide. The new subscription
                                        // typeName values (`BUILDVIZER_UNIFIED`, `BUILDVIZER_PLUS`)
                                        // are too long to share a line with the company name —
                                        // they overflow and visually collide with the email
                                        // below. Render a short label on its own row instead.
                                        const rawPlan = userInfo?.loginIdentifierDetails?.subscription?.subscriptionType?.typeName;
                                        const PLAN_SHORT_LABEL = {
                                            BUILDVIZER_UNIFIED: 'Unified',
                                            BUILDVIZER_PLUS: 'Plus',
                                        };
                                        const planLabel = PLAN_SHORT_LABEL[rawPlan] || rawPlan;
                                        return (
                                            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                                        <FaUser className="text-blue-600 text-lg" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-900 truncate">
                                                            {userInfo?.loginIdentifierDetails?.customer?.companyName || userInfo?.loginIdentifierDetails?.companyName || "User"}
                                                        </p>
                                                        <p className="text-xs text-gray-500 truncate">
                                                            {userInfo?.loginIdentifierDetails?.customer?.emailId || userInfo?.loginIdentifierDetails?.emailId || userInfo?.loginIdentifierDetails?.customer?.email || userInfo?.email || "—"}
                                                        </p>
                                                        {planLabel && (
                                                            <span className="inline-flex items-center px-2 py-0.5 mt-1.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800 max-w-full truncate">
                                                                {planLabel}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    
                                    {/* Menu Items — Settings + Sign Out only.
                                        Settings deep-links to the same Profile page that
                                        renders the sidebar (Profile / Onboarding / Role
                                        Assignment / Change Password / etc.). All other
                                        previously-flat shortcuts moved into that sidebar. */}
                                    <div className="py-1">
                                        {!isZumaleUserNav && (
                                            <Link
                                                to={profileSettingsPath}
                                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                                onClick={() => setProfileMenuOpen(false)}
                                            >
                                                <FaUserCog className="mr-3 text-gray-500" />
                                                Settings
                                            </Link>
                                        )}

                                        <button
                                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                            onClick={() => {
                                                logout();
                                                setProfileMenuOpen(false);
                                            }}
                                        >
                                            <FaSignOutAlt className="mr-3 text-gray-500" />
                                            Sign Out
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div
                    className="flex items-center text-xl font-semibold cursor-pointer md:ml-0 ml-10"
                    onClick={() => navigate("/")}
                >  </div>
            </nav>

            {/* {Trial && (
                <div className="w-full bg-yellow-500 opacity-90 font-bold text-white py-2 fixed top-16 z-40 overflow-hidden">
                    <div className="whitespace-nowrap animate-marquee">
                        {subscriptionData?.subscriptionType?.typeName || 'TRIAL'} PERIOD
                        {subscriptionData?.subscriptionType?.typeName || 'TRIAL'} PERIOD
                        {subscriptionData?.subscriptionType?.typeName || 'TRIAL'} PERIOD
                        {subscriptionData?.subscriptionType?.typeName || 'TRIAL'} PERIOD
                        {subscriptionData?.subscriptionType?.typeName || 'TRIAL'} PERIOD
                        {subscriptionData?.subscriptionType?.typeName || 'TRIAL'} PERIOD
                    </div>
                </div>
            )} */}

            {/* Interior Designer first-login portfolio setup prompt */}
            <PortfolioSetupPrompt
                isOpen={showPortfolioPrompt}
                onDismiss={dismissPortfolioPrompt}
            />

            {/* Org switcher — opened from the topbar chip. Dismissable. */}
            <CustomerPickerModal
                isOpen={orgSwitcherOpen}
                onClose={() => setOrgSwitcherOpen(false)}
                links={orgLinks}
                currentLinkId={currentLinkId}
                mode="switch"
                persona={persona}
                onConfirm={handleOrgSwitchConfirm}
            />
        </>
    );
}

export default Navbar;